import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLatest, useWindowSize } from "react-use";

import type { SelectionRange } from "metabase/query_builder/components/NativeQueryEditor/types";
import ViewSidebar from "metabase/query_builder/components/view/ViewSidebar";
import { Flex } from "metabase/ui";
import * as Lib from "metabase-lib";
import type Question from "metabase-lib/v1/Question";
import type {
  Database as ApiDatabase,
  NativeQuerySnippet,
} from "metabase-types/api";

import { DEFAULT_EDITOR_HEIGHT } from "../constants";
import { useWorkbenchActions } from "../context";
import {
  useInsertSnippetHandler,
  useSelectedText,
} from "../hooks/use-editor-selection";
import { useTabQueryMetadata } from "../hooks/use-tab-query-metadata";
import { useTabQuestion } from "../hooks/use-tab-question";
import { getActiveResult } from "../state/selectors";
import type { AdhocTabRuntime, PersistedAdhocTab } from "../types";
import { getQuestionSql } from "../utils/question";

import { EditorSidebar } from "./EditorSidebar";
import { QueryTabEditor } from "./QueryTabEditor";
import S from "./QueryTabPanel.module.css";
import { ResultArea } from "./ResultArea";
import { ResultSidebar, type ResultSidebarPanel } from "./ResultSidebar";

export type QueryTabPanelProps = {
  tab: PersistedAdhocTab;
  runtime: AdhocTabRuntime;
  isActive: boolean;
  databases: ApiDatabase[];
};

/**
 * Editor + results for one query tab.
 *
 * Laid out like the query builder's `View`: a left sidebar for chart type and
 * chart settings, the editor and results in the middle, and a right sidebar for
 * the data reference and snippets. Both sidebars are full height siblings of
 * the middle column rather than panels nested inside the results, which is what
 * makes them behave the way they do in the query builder.
 */
function QueryTabPanelInner({
  tab,
  runtime,
  isActive,
  databases,
}: QueryTabPanelProps) {
  const actions = useWorkbenchActions();
  const question = useTabQuestion(tab.databaseId, tab.sql);

  // Only the active tab loads dependent metadata; otherwise restoring a
  // ten-tab workbench would fire ten metadata requests at once.
  useTabQueryMetadata(question, isActive);

  const activeResult = getActiveResult(runtime);
  // Derived once here and passed down: the left sidebar and the result panel
  // both need the question as it was executed.
  const resultQuestion = useTabQuestion(
    activeResult?.databaseId ?? tab.databaseId,
    activeResult?.sql ?? "",
    activeResult?.display,
    activeResult?.visualizationSettings,
  );

  const [selectionRange, setSelectionRange] = useState<SelectionRange[]>([]);
  const selectedText = useSelectedText(question, selectionRange);

  const [isNativeEditorOpen, setIsNativeEditorOpen] = useState(true);
  const [chartPanel, setChartPanel] = useState<ResultSidebarPanel | null>(null);
  const [isShowingRawTable, setIsShowingRawTable] = useState(false);

  // The editor unmounts when the tab is deactivated, so its selection is gone
  // when we come back; drop our copy too, or the run button would keep
  // offering to run a selection that is no longer highlighted.
  useEffect(() => {
    if (!isActive) {
      setSelectionRange([]);
    }
  }, [isActive]);

  const [modalSnippet, setModalSnippet] = useState<NativeQuerySnippet | null>(
    null,
  );
  const [
    isDataReferenceOpen,
    { toggle: toggleDataReference, close: closeDataReference },
  ] = useDisclosure();
  const [
    isSnippetSidebarOpen,
    { toggle: toggleSnippetSidebar, close: closeSnippetSidebar },
  ] = useDisclosure();

  const handleQuestionChange = useCallback(
    (newQuestion: Question) => {
      actions.setTabQuery(
        tab.id,
        newQuestion.databaseId(),
        getQuestionSql(newQuestion),
      );
    },
    [actions, tab.id],
  );

  const handleInsertSnippet = useInsertSnippetHandler({
    question,
    selectionRange,
    onChange: handleQuestionChange,
  });

  const runningResult = runtime.results.find(
    (result) => result.status === "running",
  );
  const isRunning = runningResult != null;
  const sql = tab.sql;
  const isRunnable =
    tab.databaseId != null &&
    (selectedText.trim() !== "" || sql.trim() !== "") &&
    Lib.canRun(question.query(), question.type());

  // The editor's run button and the right-click "Run selection" menu both call
  // this. Reading through a ref keeps the callback identity stable, so those
  // upstream components don't see a new handler on every keystroke.
  const runStateRef = useLatest({ selectedText, sql, tabId: tab.id });
  const handleRun = useCallback(() => {
    const { selectedText, sql, tabId } = runStateRef.current;
    // A selection wins over the whole editor, so you can highlight one
    // statement in a long script and run just that.
    actions.runQuery(tabId, selectedText.trim() !== "" ? selectedText : sql);
  }, [actions, runStateRef]);

  const runningResultRef = useLatest(runningResult);
  const handleCancel = useCallback(() => {
    const running = runningResultRef.current;
    if (running) {
      actions.cancelResult(running.id);
    }
  }, [actions, runningResultRef]);

  const runnableRef = useLatest({ isRunnable, isRunning });
  const handleRunOrCancel = useCallback(() => {
    const { isRunnable, isRunning } = runnableRef.current;
    if (isRunning) {
      handleCancel();
    } else if (isRunnable) {
      handleRun();
    }
  }, [handleCancel, handleRun, runnableRef]);

  // Guarded by `isActive`: hidden panels stay mounted, and must not react to
  // the shortcut. The empty second argument stops Mantine from ignoring the
  // event just because it originated inside the editor.
  useHotkeys([["mod+Enter", () => isActive && handleRunOrCancel()]], []);

  const handleToggleDataReference = useCallback(() => {
    closeSnippetSidebar();
    toggleDataReference();
  }, [closeSnippetSidebar, toggleDataReference]);

  const handleToggleSnippetSidebar = useCallback(() => {
    closeDataReference();
    toggleSnippetSidebar();
  }, [closeDataReference, toggleSnippetSidebar]);

  const handleResizeStop = useCallback(
    (height: number) => actions.setTabEditorHeight(tab.id, height),
    [actions, tab.id],
  );

  const handleToggleChartPanel = useCallback(
    (panel: ResultSidebarPanel) =>
      setChartPanel((current) => (current === panel ? null : panel)),
    [],
  );

  const handleSelectVisualization = useCallback(
    (updated: Question) => {
      if (activeResult) {
        actions.setResultDisplay(
          tab.id,
          activeResult.id,
          updated.display(),
          updated.settings(),
        );
        setIsShowingRawTable(false);
      }
    },
    [actions, activeResult, tab.id],
  );

  const handleReplaceVisualizationSettings = useCallback(
    (settings: Parameters<typeof actions.setResultSettings>[2]) => {
      if (activeResult) {
        actions.setResultSettings(tab.id, activeResult.id, settings);
      }
    },
    [actions, activeResult, tab.id],
  );

  const defaultEditorHeight = useDefaultEditorHeight();
  const editorHeight = tab.editorHeight ?? defaultEditorHeight;

  return (
    <Flex className={S.panel} h="100%" w="100%">
      <ViewSidebar side="left" isOpen={chartPanel != null}>
        <ResultSidebar
          panel={chartPanel}
          result={activeResult}
          question={resultQuestion}
          onSelectVisualization={handleSelectVisualization}
          onChangeSettings={handleReplaceVisualizationSettings}
          onChangePanel={setChartPanel}
        />
      </ViewSidebar>

      <Flex className={S.main} direction="column">
        {/*
         * Only the active tab keeps an editor mounted.
         *
         * `CodeMirrorEditor.getSelectionTarget()` resolves the right-click
         * menu's anchor with a document-wide `querySelector`, so a second
         * mounted editor holding a stale selection would anchor the menu to a
         * hidden, zero-sized element in the page corner. Keeping exactly one
         * editor in the document keeps that lookup unambiguous.
         *
         * The result area below stays mounted either way, which is what
         * matters for `DebouncedFrame`'s width measurement.
         */}
        {isActive && (
          <QueryTabEditor
            question={question}
            databases={databases}
            editorHeight={editorHeight}
            isRunnable={isRunnable}
            isRunning={isRunning}
            isNativeEditorOpen={isNativeEditorOpen}
            isShowingDataReference={isDataReferenceOpen}
            isShowingSnippetSidebar={isSnippetSidebarOpen}
            modalSnippet={modalSnippet}
            nativeEditorSelectedText={selectedText}
            onChange={handleQuestionChange}
            onRunQuery={handleRun}
            onCancelQuery={handleCancel}
            onResizeStop={handleResizeStop}
            onSetNativeEditorOpen={setIsNativeEditorOpen}
            onToggleEditor={() => setIsNativeEditorOpen((open) => !open)}
            onToggleDataReference={handleToggleDataReference}
            onToggleSnippetSidebar={handleToggleSnippetSidebar}
            onInsertSnippet={handleInsertSnippet}
            onChangeModalSnippet={setModalSnippet}
            onChangeSelection={setSelectionRange}
          />
        )}
        <ResultArea
          tabId={tab.id}
          runtime={runtime}
          isActive={isActive}
          activeResult={activeResult}
          resultQuestion={resultQuestion}
          chartPanel={chartPanel}
          isShowingRawTable={isShowingRawTable}
          onToggleChartPanel={handleToggleChartPanel}
          onToggleRawTable={setIsShowingRawTable}
          onSelectVisualization={handleSelectVisualization}
          onChangeVisualizationSettings={handleReplaceVisualizationSettings}
        />
      </Flex>

      <ViewSidebar
        side="right"
        isOpen={isActive && (isDataReferenceOpen || isSnippetSidebarOpen)}
      >
        <EditorSidebar
          question={question}
          isDataReferenceOpen={isDataReferenceOpen}
          isSnippetSidebarOpen={isSnippetSidebarOpen}
          onToggleDataReference={handleToggleDataReference}
          onToggleSnippetSidebar={handleToggleSnippetSidebar}
          onChangeModalSnippet={setModalSnippet}
          onInsertSnippet={handleInsertSnippet}
        />
      </ViewSidebar>
    </Flex>
  );
}

const HEADER_HEIGHT = 180;

function useDefaultEditorHeight() {
  const { height: windowHeight } = useWindowSize();
  return useMemo(
    () =>
      Math.max(
        200,
        Math.min(0.45 * (windowHeight - HEADER_HEIGHT), DEFAULT_EDITOR_HEIGHT),
      ),
    [windowHeight],
  );
}

export const QueryTabPanel = memo(QueryTabPanelInner);
